import { useEffect } from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import { PageHero } from "@/components/marketing/sections/page-hero";
import { IltCourseList } from "@/features/instructor-led/components/IltCourseList";
import { IltLoadError, IltNotFound } from "@/features/instructor-led/components/IltNotFound";
import { fetchFeatureFlags } from "@/hooks/useFeatureFlags";
import { getInstructorLedListUpcomingCoursesQueryOptions } from "@/services/api/instructor-led/instructor-led";
import { queryClient } from "@/services/queryClient";

export const Route = createFileRoute("/_marketing/face-to-face-training")({
  // Face-to-face training is hidden while the ilt flag is off (the API 404s it too).
  beforeLoad: async () => {
    const { ilt } = await fetchFeatureFlags();
    if (!ilt) throw notFound();
  },
  loader: async () => {
    try {
      // Always asked afresh: sessions start, fill up and are cancelled, and the API answers 404 once the
      // ilt flag is switched off, which a cached list would hide.
      return await queryClient.fetchQuery({
        ...getInstructorLedListUpcomingCoursesQueryOptions(),
        staleTime: 0,
      });
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) throw notFound();
      throw error;
    }
  },
  notFoundComponent: IltNotFound,
  errorComponent: IltLoadError,
  component: FaceToFaceTrainingPage,
});

/**
 * FaceToFaceTrainingPage - The published instructor-led courses with upcoming sessions, soonest next
 * session first. Open to everyone, signed in or not.
 */
function FaceToFaceTrainingPage() {
  const courses = Route.useLoaderData();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <>
      <PageHero
        title="Face-to-face training"
        lead="Training courses run in person by our trainers. Choose a course to see its upcoming sessions and book your place."
      />
      <IltCourseList courses={courses} />
    </>
  );
}
